import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../../../components/Input";
import type { BannerType, InAppAnnouncementBanner as Banner, VersionFilter, VersionOperator } from "../services/settingsTypes";
import Button from "../../../components/Button";
import { Select } from "../../../components/Select";
import { getLegacyAppsMatch, inAppAnnouncementBannerService, LAST_LEGACY_APP_VERSION } from "../services/inAppAnnouncementBannerService";
import { Toggle } from "../../../components/Toggle";
import { isValidSemVer } from "../../../utils/semver";

const VERSION_OPERATOR_LABELS: Record<VersionOperator, string> = {
    lte: "<=",
    eq: "=",
    gte: ">=",
};
const ALL_VERSIONS_LABEL = "All versions";

const DEFAULT_BANNER: Banner = {
    bannerText: "",
    bannerType: "info",
    showEveryTime: false,
    versionFilter: null,
    showOnLegacyApps: true,
};

const isSameBanner = (a: Banner, b: Banner) =>
    a.bannerText === b.bannerText &&
    a.bannerType === b.bannerType &&
    a.showEveryTime === b.showEveryTime &&
    a.showOnLegacyApps === b.showOnLegacyApps &&
    a.versionFilter?.operator === b.versionFilter?.operator &&
    a.versionFilter?.version === b.versionFilter?.version;

export const InAppAnnouncementBanner = () => {
    const originalBanner = useRef<Banner>(DEFAULT_BANNER);
    const [bannerText, setBannerText] = useState("");
    const [bannerType, setBannerType] = useState<BannerType>("info");
    const [showEveryTime, setShowEveryTime] = useState(false);
    const [versionOperator, setVersionOperator] = useState<VersionOperator | null>(null);
    const [version, setVersion] = useState("");
    const [showOnLegacyApps, setShowOnLegacyApps] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const versionFilter = useMemo<VersionFilter | null>(() => {
        return versionOperator ? { operator: versionOperator, version: version.trim() } : null;
    }, [versionOperator, version]);

    const banner = useMemo<Banner>(() => ({
        bannerText,
        bannerType,
        showEveryTime,
        versionFilter,
        showOnLegacyApps,
    }), [bannerText, bannerType, showEveryTime, versionFilter, showOnLegacyApps]);

    const isVersionValid = !versionFilter || isValidSemVer(versionFilter.version);
    const legacyAppsMatch = isVersionValid ? getLegacyAppsMatch(versionFilter) : null;
    const isChanged = !isSameBanner(banner, originalBanner.current);

    useEffect(() => {
        const fetchBanner = async () => {
            setIsLoading(true);
            const savedBanner = await inAppAnnouncementBannerService.getInAppAnnouncementBanner();
            if (savedBanner && savedBanner.bannerText && savedBanner.bannerType) {
                setBannerText(savedBanner.bannerText);
                setBannerType(savedBanner.bannerType);
                setShowEveryTime(savedBanner.showEveryTime);
                setVersionOperator(savedBanner.versionFilter?.operator ?? null);
                setVersion(savedBanner.versionFilter?.version ?? "");
                setShowOnLegacyApps(savedBanner.showOnLegacyApps);
                originalBanner.current = savedBanner;
            }
            setIsLoading(false);
        };
        fetchBanner();
    }, []);

    const handleSave = async () => {
        setIsDisabled(true);
        if (isChanged && isVersionValid) {
            await inAppAnnouncementBannerService.updateInAppAnnouncementBanner(banner);
            originalBanner.current = banner;
        }
        setIsDisabled(false);
    };

    const handleVersionOperatorChange = (label: string) => {
        const operator = (Object.keys(VERSION_OPERATOR_LABELS) as VersionOperator[])
            .find((key) => VERSION_OPERATOR_LABELS[key] === label);
        setVersionOperator(operator ?? null);
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-bold text-gray-900 mb-2">In App Announcement Banner</h2>
            {isLoading ? <div className="flex justify-center items-center h-full">Loading...</div> : (
                <div className="flex flex-col gap-2">
                    {/* The app shows the banner as one paragraph, so line breaks are not allowed. */}
                    <textarea
                        value={bannerText}
                        onChange={(e) => setBannerText(e.target.value.replace(/\r?\n/g, " "))}
                        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                        placeholder="Banner Text"
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">App Version</label>
                        <Select
                            value={versionOperator ? VERSION_OPERATOR_LABELS[versionOperator] : ALL_VERSIONS_LABEL}
                            onChange={handleVersionOperatorChange}
                            options={[ALL_VERSIONS_LABEL, ...Object.values(VERSION_OPERATOR_LABELS)]}
                        />
                        {versionOperator && (
                            <div className="w-32">
                                <Input value={version} onChange={setVersion} placeholder="1.8.0" />
                            </div>
                        )}
                        {!isVersionValid && <span className="text-sm text-red-600">Use the format 1.2.3</span>}
                    </div>
                    {legacyAppsMatch === "some" && (
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">
                                Show on apps {"<="} {LAST_LEGACY_APP_VERSION} (they cannot check the version, so all of them get the banner)
                            </label>
                            <Toggle checked={showOnLegacyApps} onChange={setShowOnLegacyApps} />
                        </div>
                    )}
                    {legacyAppsMatch !== "some" && legacyAppsMatch !== null && (
                        <p className="text-sm text-gray-500">
                            Apps {"<="} {LAST_LEGACY_APP_VERSION}: {legacyAppsMatch === "all" ? "shown" : "hidden"}
                        </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <Select
                            value={bannerType}
                            onChange={(value) => setBannerType(value as BannerType)}
                            options={["info", "warning", "error", "success"]}
                        />
                        <label className="text-sm font-medium text-gray-700">Show Every Time</label>
                        <Toggle checked={showEveryTime} onChange={setShowEveryTime} />
                        <Button onClick={handleSave} disabled={isDisabled || !isChanged || !isVersionValid}>Save</Button>
                    </div>
                </div>
            )}
        </div>
    );
};
